import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import type {
  AxonRunStatusFilter,
  AxonRunTimeFilter,
  AxonRunView,
  AxonRunsModel,
  PlatformHealthAxonRunsResponse,
} from '@/pages/platform-health/types';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  CircleSlash,
  Clock3,
  ExternalLink,
  FileWarning,
  RefreshCw,
  Search,
} from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

const REFRESH_INTERVAL_MS = 60 * 1000;
const SUBSYSTEMS = ['axon', 'aquix', 'raven', 'pulse', 'lattice', 'prism', 'vital', 'mantis'];
const STATUSES: AxonRunStatusFilter[] = [
  'all',
  'expected',
  'active',
  'complete',
  'incomplete',
  'failed',
  'errored',
  'canceled',
  'skipped',
];
const TIME_RANGES: AxonRunTimeFilter[] = ['24h', '7d', '30d', 'all'];

function display(value: string | null) {
  return (value ?? 'unknown').replace(/_/g, ' ');
}

function relative(iso: string | null) {
  if (!iso || Number.isNaN(Date.parse(iso))) return 'time unknown';
  const minutes = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 60_000));
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function duration(value: number | null) {
  if (value === null) return 'not reported';
  if (value < 1_000) return `${value}ms`;
  const seconds = Math.round(value / 1_000);
  return seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}m`;
}

function tone(run: AxonRunView) {
  if (run.status.outcome === 'fail' || run.status.outcome === 'error') return 'red';
  if (run.kind === 'expected' || run.status.completeness !== 'complete') return 'amber';
  if (run.status.outcome === 'pass') return 'green';
  return 'neutral';
}

function Badge({
  children,
  variant = 'neutral',
}: {
  children: React.ReactNode;
  variant?: 'neutral' | 'red' | 'amber' | 'green' | 'blue';
}) {
  const colors = {
    neutral: 'bg-gray-100 text-gray-700 dark:bg-zinc-700 dark:text-gray-200',
    red: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
    green: 'bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-200',
    blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-200',
  };
  return (
    <span
      className={cn(
        'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide',
        colors[variant]
      )}
    >
      {children}
    </span>
  );
}

function Axis({ label, value }: { label: string; value: string | null }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-xs font-medium text-gray-900 dark:text-white">{display(value)}</dd>
    </div>
  );
}

function RunRow({
  run,
  selected,
  onSelect,
}: {
  run: AxonRunView;
  selected: boolean;
  onSelect?: (runKey: string | null) => void;
}) {
  const Icon =
    run.kind === 'expected'
      ? Clock3
      : run.status.outcome === 'fail' || run.status.outcome === 'error'
        ? AlertTriangle
        : run.status.completeness === 'complete'
          ? CheckCircle2
          : FileWarning;
  return (
    <details
      data-testid="axon-run-row"
      data-run-key={run.key}
      open={selected || undefined}
      onToggle={(event) => {
        if (!onSelect) return;
        const open = (event.currentTarget as HTMLDetailsElement).open;
        if (open && !selected) onSelect(run.key);
        else if (!open && selected) onSelect(null);
      }}
      className="group border-b border-gray-200 last:border-b-0 dark:border-zinc-700"
    >
      <summary className="flex cursor-pointer list-none items-start gap-3 px-4 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-500">
        <Icon
          className={cn(
            'mt-0.5 h-5 w-5 shrink-0',
            tone(run) === 'red'
              ? 'text-red-600'
              : tone(run) === 'amber'
                ? 'text-amber-600'
                : tone(run) === 'green'
                  ? 'text-green-600'
                  : 'text-gray-500'
          )}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-950 dark:text-white">
              {run.checkName}
            </span>
            <Badge variant={tone(run)}>{run.lifecycle}</Badge>
            {run.status.outcome ? <Badge variant={tone(run)}>{run.status.outcome}</Badge> : null}
            {run.provenance.kind === 'compatibility' ? (
              <Badge variant="blue">compatibility</Badge>
            ) : null}
            {run.overdue ? <Badge variant="red">overdue</Badge> : null}
          </div>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{run.summary}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {run.subsystemName} · {run.testDefinitionId ?? run.checkId} · {relative(run.latestAt)}
          </p>
        </div>
        <div className="shrink-0 text-right text-xs text-gray-500 dark:text-gray-400">
          <p>{run.releaseId ?? 'release unknown'}</p>
          <p>
            attempt {run.attempt}
            {run.retryCount ? ` · ${run.retryCount} retr${run.retryCount === 1 ? 'y' : 'ies'}` : ''}
          </p>
        </div>
        <ChevronRight
          className="mt-1 h-4 w-4 shrink-0 text-gray-400 transition-transform group-open:rotate-90"
          aria-hidden="true"
        />
      </summary>
      <div className="border-t border-gray-100 bg-gray-50/70 px-4 py-4 dark:border-zinc-700 dark:bg-zinc-900/40">
        <dl className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Axis label="Outcome" value={run.status.outcome} />
          <Axis label="Applicability" value={run.status.applicability} />
          <Axis label="Freshness" value={run.status.freshness} />
          <Axis label="Completeness" value={run.status.completeness} />
          <Axis label="Policy" value={run.status.policy} />
        </dl>
        <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
          <div>
            <dt className="text-gray-500">Outcome category</dt>
            <dd className="mt-0.5 font-medium">{display(run.outcomeCategory)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Duration</dt>
            <dd className="mt-0.5 font-medium">{duration(run.durationMs)}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Tests</dt>
            <dd className="mt-0.5 font-medium">
              {run.passedCount ?? '?'} passed · {run.failedCount ?? '?'} failed
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Owner</dt>
            <dd className="mt-0.5 font-medium">{run.owner.displayName}</dd>
          </div>
        </dl>
        <dl className="mt-4 grid gap-2 rounded border border-gray-200 bg-white p-3 text-xs dark:border-zinc-700 dark:bg-zinc-800 sm:grid-cols-2">
          <div>
            <dt className="text-gray-500">Run / expectation</dt>
            <dd className="break-all font-mono text-[11px]">
              {run.runId ?? run.expectationId ?? 'not declared'}
            </dd>
          </div>
          <div>
            <dt className="text-gray-500">Parent run</dt>
            <dd className="break-all font-mono text-[11px]">{run.parentRunId ?? 'none'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Test SHA</dt>
            <dd className="break-all font-mono text-[11px]">{run.testSha ?? 'not declared'}</dd>
          </div>
          <div>
            <dt className="text-gray-500">Source ref / producer run</dt>
            <dd className="break-all font-mono text-[11px]">
              {run.sourceRef ?? 'not declared'} · {run.producerRunId ?? 'not declared'}
            </dd>
          </div>
        </dl>
        {run.artifactRetention === 'github_retention_limited' ? (
          <div
            data-testid="github-artifact-limitation"
            className="mt-4 rounded border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
          >
            GitHub artifacts are retention-limited and may already have expired. The durable AXON
            compatibility record preserves the summary, counts, SHA, and run link—not screenshots or
            traces.
          </div>
        ) : null}
        {run.provenance.limitations.length > 0 ? (
          <section className="mt-4 text-xs">
            <h3 className="font-semibold text-gray-950 dark:text-white">Known limitations</h3>
            <ul className="mt-1 list-disc pl-5 text-gray-600 dark:text-gray-300">
              {run.provenance.limitations.map((limitation) => (
                <li key={limitation}>{display(limitation)}</li>
              ))}
            </ul>
          </section>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-3 text-xs">
          <a
            href={run.workbenchLink}
            className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
          >
            {run.subsystemId === 'aquix'
              ? 'Open AQUIX evidence workbench'
              : 'Open owning workbench'}
            <ExternalLink className="h-3 w-3" aria-hidden="true" />
          </a>
          {run.evidence.map((item) =>
            item.uri && item.availability !== 'missing' ? (
              <a
                key={item.evidenceReferenceId}
                href={item.uri}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline dark:text-blue-400"
              >
                {item.kind} evidence{item.availability === 'expired' ? ' (expired)' : ''}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
              </a>
            ) : (
              <span key={item.evidenceReferenceId} className="text-amber-700 dark:text-amber-300">
                Missing evidence: {item.evidenceReferenceId}
              </span>
            )
          )}
          {run.relatedFindingIds.map((findingId) => (
            <a
              key={findingId}
              href={`/admin/platform-health/critical?finding=${encodeURIComponent(findingId)}`}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Related finding {findingId}
            </a>
          ))}
        </div>
        {run.history.length > 0 ? (
          <section className="mt-4">
            <h3 className="text-xs font-semibold text-gray-950 dark:text-white">
              Lifecycle history
            </h3>
            <ol className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
              {run.history.map((event) => (
                <li key={event.eventId}>
                  #{event.sequence} {display(event.lifecycle)} · {relative(event.occurredAt)}
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        <p className="mt-4 break-all text-[10px] text-gray-500 dark:text-gray-400">
          Inputs: {run.inputRecordIds.join(', ') || 'none'}
        </p>
      </div>
    </details>
  );
}

export function AxonRunsContent({
  payload,
  selectedRunKey = null,
  onSelectRun,
}: {
  payload: PlatformHealthAxonRunsResponse;
  selectedRunKey?: string | null;
  onSelectRun?: (runKey: string | null) => void;
}) {
  return (
    <div className="space-y-4">
      {!payload.available ? (
        <div
          data-testid="axon-runs-unavailable"
          className="rounded-lg border border-gray-300 bg-gray-50 p-4 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-gray-100"
        >
          <strong>AXON run history is unavailable.</strong> An unreadable evidence plane is not an
          empty or successful run history.
        </div>
      ) : null}
      {payload.partial ? (
        <div
          data-testid="axon-runs-partial"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Partial run context.</strong>
          <ul className="mt-2 list-disc pl-5 text-xs">
            {payload.gaps.map((gap, index) => (
              <li key={`${gap.code}:${index}`}>{gap.message}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-6">
        {[
          ['Expected / not started', payload.counts.expected],
          ['Active', payload.counts.active],
          ['Complete', payload.counts.complete],
          ['Incomplete / missing', payload.counts.incomplete],
          ['Failed / errored', payload.counts.failedOrErrored],
          ['Compatibility', payload.counts.compatibility],
        ].map(([label, value]) => (
          <div
            key={String(label)}
            className="rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
          >
            <dt className="text-xs text-gray-500">{label}</dt>
            <dd className="mt-1 text-2xl font-semibold tabular-nums text-gray-950 dark:text-white">
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {payload.available && payload.runs.length === 0 ? (
        <div
          data-testid="axon-runs-empty"
          className="rounded-lg border border-gray-200 bg-white p-12 text-center dark:border-zinc-700 dark:bg-zinc-800"
        >
          <CircleSlash className="mx-auto h-8 w-8 text-gray-400" aria-hidden="true" />
          <p className="mt-4 text-sm font-medium text-gray-950 dark:text-white">
            No runs match these filters
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Expected-but-not-started runs remain visible when the expectation denominator is
            present.
          </p>
        </div>
      ) : null}
      {payload.runs.length > 0 ? (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-800">
          {payload.runs.map((run) => (
            <RunRow
              key={run.key}
              run={run}
              selected={selectedRunKey === run.key}
              onSelect={onSelectRun}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function SelectFilter({
  label,
  value,
  values,
  onChange,
}: {
  label: string;
  value: string;
  values: readonly string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="text-xs text-gray-600 dark:text-gray-300">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-9 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-800 dark:text-white"
      >
        {values.map((item) => (
          <option key={item} value={item}>
            {item === 'all' ? `All ${label.toLowerCase()}` : display(item)}
          </option>
        ))}
      </select>
    </label>
  );
}

export function TestRunsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const model: AxonRunsModel =
    searchParams.get('readModel') === 'compatibility' ? 'compatibility' : 'axon';
  const environment = searchParams.get('environment') ?? '';
  const subsystem = searchParams.get('subsystem') ?? 'all';
  const status = (searchParams.get('status') ?? 'all') as AxonRunStatusFilter;
  const time = (searchParams.get('time') ?? '30d') as AxonRunTimeFilter;
  const release = searchParams.get('release') ?? '';
  const finding = searchParams.get('finding') ?? '';
  const search = searchParams.get('search') ?? '';
  const selectedRunKey = searchParams.get('run');

  const query = useQuery({
    queryKey: [
      'platform-health',
      model,
      'runs',
      environment,
      subsystem,
      status,
      time,
      release,
      finding,
      search,
    ],
    queryFn: () =>
      api.getPlatformHealthRuns(model, {
        environment: environment || undefined,
        subsystem: subsystem === 'all' ? undefined : subsystem,
        status,
        time,
        release: release || undefined,
        finding: finding || undefined,
        search: search || undefined,
      }),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
  const selectedRunInList =
    query.data?.data.runs.some((run) => run.key === selectedRunKey) ?? false;
  const detailQuery = useQuery({
    queryKey: ['platform-health', model, 'run-detail', environment, selectedRunKey],
    queryFn: () => api.getPlatformHealthRunDetail(model, selectedRunKey!, environment || undefined),
    enabled: Boolean(selectedRunKey),
    refetchInterval: REFRESH_INTERVAL_MS,
    retry: false,
  });

  function setParam(name: string, value: string | null, defaultValue?: string) {
    const next = new URLSearchParams(searchParams);
    if (!value || value === defaultValue) next.delete(name);
    else next.set(name, value);
    if (name !== 'run') next.delete('run');
    setSearchParams(next);
  }

  function switchModel() {
    setParam('readModel', model === 'axon' ? 'compatibility' : null);
  }

  return (
    <div className="px-4 py-0">
      <PageHeader
        title="Runs"
        subtitle="Expected and observed run lifecycle across every APHID subsystem"
      />
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {query.data?.data.generatedAt
            ? `Updated ${relative(query.data.data.generatedAt)}`
            : 'Loading run history…'}
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={switchModel}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            {model === 'compatibility' ? 'Return to AXON' : 'Compatibility view'}
          </button>
          <button
            type="button"
            onClick={() => void query.refetch()}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:text-gray-200 dark:hover:bg-zinc-700"
          >
            <RefreshCw
              className={cn('h-3.5 w-3.5', query.isFetching && 'animate-spin')}
              aria-hidden="true"
            />
            Refresh
          </button>
        </div>
      </div>
      {model === 'compatibility' ? (
        <div
          data-testid="compatibility-runs-model"
          className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <strong>Compatibility read model.</strong> These durable Playwright/check-sink
          observations cannot reconstruct expected-but-missing runs, freshness, applicability,
          parent runs, or finding lifecycle. They never silently replace AXON.
        </div>
      ) : null}
      <section
        aria-label="Run filters"
        className="mb-4 rounded-lg border border-gray-200 bg-white p-3 dark:border-zinc-700 dark:bg-zinc-800"
      >
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-52 flex-1">
            <span className="sr-only">Search runs</span>
            <Search
              className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400"
              aria-hidden="true"
            />
            <input
              aria-label="Search runs"
              value={search}
              onChange={(event) => setParam('search', event.target.value)}
              placeholder="Run, suite, SHA, release, or summary"
              className="h-9 w-full rounded-md border border-gray-300 bg-white pl-9 pr-3 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
          <SelectFilter
            label="Environment"
            value={environment || 'default'}
            values={['default', 'production', 'staging', 'ci', 'development', 'unknown']}
            onChange={(value) => setParam('environment', value, 'default')}
          />
          <SelectFilter
            label="Subsystem"
            value={subsystem}
            values={['all', ...SUBSYSTEMS]}
            onChange={(value) => setParam('subsystem', value, 'all')}
          />
          <SelectFilter
            label="Status"
            value={status}
            values={STATUSES}
            onChange={(value) => setParam('status', value, 'all')}
          />
          <SelectFilter
            label="Time"
            value={time}
            values={TIME_RANGES}
            onChange={(value) => setParam('time', value, '30d')}
          />
          <label className="text-xs text-gray-600 dark:text-gray-300">
            <span className="sr-only">Release</span>
            <input
              aria-label="Release"
              value={release}
              onChange={(event) => setParam('release', event.target.value)}
              placeholder="Release identity"
              className="h-9 w-40 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
            />
          </label>
        </div>
        {finding ? (
          <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            Related finding: <code>{finding}</code>
            <button
              type="button"
              onClick={() => setParam('finding', null)}
              className="font-medium text-blue-600 hover:underline dark:text-blue-400"
            >
              Clear
            </button>
          </div>
        ) : null}
      </section>
      {query.isError ? (
        <div
          data-testid="platform-health-runs-error"
          className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          Couldn&apos;t load run history: {(query.error as Error)?.message || 'unknown error'}
        </div>
      ) : null}
      {query.isLoading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-12 text-center text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400">
          Loading AXON run history…
        </div>
      ) : null}
      {selectedRunKey && !selectedRunInList && detailQuery.isLoading ? (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-gray-400">
          Loading the selected run from bounded history…
        </div>
      ) : null}
      {selectedRunKey && !selectedRunInList && detailQuery.isError ? (
        <div
          data-testid="platform-health-run-detail-error"
          className="mb-4 rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          Couldn&apos;t resolve selected run {selectedRunKey}:{' '}
          {(detailQuery.error as Error)?.message || 'unknown error'}
        </div>
      ) : null}
      {selectedRunKey && !selectedRunInList && detailQuery.data?.data.run.key === selectedRunKey ? (
        <section data-testid="selected-run-detail" className="mb-4 space-y-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-300">
            Selected run from bounded detail history (outside the current list page)
          </p>
          <div className="overflow-hidden rounded-xl border border-blue-300 bg-white dark:border-blue-800 dark:bg-zinc-800">
            <RunRow
              run={detailQuery.data.data.run}
              selected
              onSelect={(runKey) => setParam('run', runKey)}
            />
          </div>
        </section>
      ) : null}
      {query.data?.data ? (
        <AxonRunsContent
          payload={query.data.data}
          selectedRunKey={selectedRunKey}
          onSelectRun={(runKey) => setParam('run', runKey)}
        />
      ) : null}
    </div>
  );
}
